import React from 'react';
import {
  Paper,
  TextField,
  Box,
  Typography,
  alpha,
  InputAdornment,
  IconButton
} from '@mui/material';
import { 
  Computer as ComputerIcon,
  Add as AddIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';

const GuicheSelector = ({ 
  guicheSelecionado, 
  handleGuicheChange, 
  customTheme,
  sidebarOpen = false // Nova prop para controlar responsividade
}) => {
  // Número máximo e mínimo de guichês
  const maxGuiches = 99;
  const minGuiches = 1;
  
  // Função para lidar com a mudança de valor do campo
  const handleChange = (event) => {
    const valor = parseInt(event.target.value);
    // Verificar se é um número válido e está dentro dos limites
    if (!isNaN(valor) && valor >= minGuiches && valor <= maxGuiches) {
      handleGuicheChange({ target: { value: valor } });
    }
  };
  
  // Função para incrementar o guichê
  const incrementar = () => {
    if (guicheSelecionado < maxGuiches) {
      handleGuicheChange({ target: { value: guicheSelecionado + 1 } });
    }
  };
  
  // Função para decrementar o guichê
  const decrementar = () => {
    if (guicheSelecionado > minGuiches) {
      handleGuicheChange({ target: { value: guicheSelecionado - 1 } });
    }
  };
  
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, sm: 2 },
        borderRadius: 3,
        border: theme => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        display: 'flex',
        flexDirection: { 
          xs: 'column', 
          sm: sidebarOpen ? 'column' : 'row',
          md: 'row' 
        },
        alignItems: 'center',
        gap: { xs: 1, sm: sidebarOpen ? 1 : 0, md: 0 },
        width: { xs: '100%', sm: 'auto' },
        minWidth: { 
          sm: sidebarOpen ? '180px' : '200px', 
          md: '200px' 
        },
        maxWidth: { 
          xs: '100%', 
          sm: sidebarOpen ? '220px' : 'none',
          md: 'none'
        }
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mr: { xs: 0, sm: sidebarOpen ? 0 : 2, md: 2 },
        mb: { xs: 0.5, sm: sidebarOpen ? 0.5 : 0, md: 0 }
      }}>
        <ComputerIcon sx={{ 
          color: customTheme.palette.primary.main, 
          mr: 1,
          fontSize: { 
            xs: '1.2rem', 
            sm: sidebarOpen ? '1.1rem' : '1.5rem',
            md: '1.5rem' 
          }
        }} />
        <Typography 
          variant="subtitle2" 
          fontWeight={600}
          sx={{ 
            fontSize: { 
              xs: '0.875rem', 
              sm: sidebarOpen ? '0.8125rem' : '0.9375rem',
              md: '0.9375rem' 
            } 
          }}
        >
          Guichê:
        </Typography>
      </Box>
      
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: { xs: 'center', sm: 'flex-start' }
      }}>
        <IconButton 
          size="small" 
          onClick={decrementar}
          disabled={guicheSelecionado <= minGuiches}
          sx={{ 
            color: customTheme.palette.primary.main,
            width: { xs: 32, sm: 36 },
            height: { xs: 32, sm: 36 },
            '&.Mui-disabled': {
              color: alpha(customTheme.palette.text.disabled, 0.3)
            }
          }}
        >
          <RemoveIcon fontSize="small" />
        </IconButton>
        
        <TextField
          type="number"
          variant="outlined"
          size="small"
          value={guicheSelecionado}
          onChange={handleChange}
          inputProps={{ 
            min: minGuiches, 
            max: maxGuiches,
            step: 1,
            style: { 
              textAlign: 'center',
              fontWeight: 600,
              padding: '8px 0px',
              width: '50px',
              fontSize: '14px'
            }
          }}
          sx={{
            mx: 0.5,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '& fieldset': {
                borderColor: alpha(customTheme.palette.primary.main, 0.2),
              },
              '&:hover fieldset': {
                borderColor: alpha(customTheme.palette.primary.main, 0.3),
              },
              '&.Mui-focused fieldset': {
                borderColor: customTheme.palette.primary.main,
              },
              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                '-webkit-appearance': 'none',
                margin: 0,
              },
              '& input[type=number]': {
                '-moz-appearance': 'textfield',
              },
            },
          }}
        />
        
        <IconButton 
          size="small" 
          onClick={incrementar}
          disabled={guicheSelecionado >= maxGuiches}
          sx={{ 
            color: customTheme.palette.primary.main,
            width: { xs: 32, sm: 36 },
            height: { xs: 32, sm: 36 },
            '&.Mui-disabled': {
              color: alpha(customTheme.palette.text.disabled, 0.3)
            }
          }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>
    </Paper>
  );
};

export default GuicheSelector; 